import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { cva } from 'class-variance-authority';
import * as React2 from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';

// src/lib/cn.ts
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
var buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary: "bg-primary text-primary-foreground shadow-sm hover:bg-brand-800 active:bg-brand-900",
        accent: "bg-accent text-accent-foreground shadow-sm hover:bg-clay-600 active:bg-clay-700",
        outline: "border border-input bg-card text-foreground hover:bg-muted",
        ghost: "text-foreground hover:bg-muted",
        destructive: "bg-destructive text-destructive-foreground shadow-sm hover:opacity-90",
        link: "text-primary underline-offset-4 hover:underline"
      },
      size: {
        sm: "h-9 px-3",
        md: "h-11 px-5",
        lg: "h-12 px-6 text-base",
        icon: "h-11 w-11"
      }
    },
    defaultVariants: { variant: "primary", size: "md" }
  }
);
var Button = React2.forwardRef(
  ({ className, variant, size, ...props }, ref) => /* @__PURE__ */ jsx(
    "button",
    {
      ref,
      className: cn(buttonVariants({ variant, size }), className),
      ...props
    }
  )
);
Button.displayName = "Button";
var Card = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "div",
  {
    ref,
    className: cn(
      "rounded-lg border border-border bg-card text-card-foreground shadow-sm",
      className
    ),
    ...props
  }
));
Card.displayName = "Card";
var CardHeader = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex flex-col gap-1.5 p-6", className), ...props }));
CardHeader.displayName = "CardHeader";
var CardTitle = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "h3",
  {
    ref,
    className: cn("font-display text-xl font-medium tracking-tight", className),
    ...props
  }
));
CardTitle.displayName = "CardTitle";
var CardDescription = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("p", { ref, className: cn("text-sm text-muted-foreground", className), ...props }));
CardDescription.displayName = "CardDescription";
var CardContent = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("p-6 pt-0", className), ...props }));
CardContent.displayName = "CardContent";
var CardFooter = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx("div", { ref, className: cn("flex items-center p-6 pt-0", className), ...props }));
CardFooter.displayName = "CardFooter";
var Input = React2.forwardRef(({ className, type, ...props }, ref) => /* @__PURE__ */ jsx(
  "input",
  {
    ref,
    type,
    className: cn(
      "flex h-11 w-full rounded-md border border-input bg-card px-3.5 py-2 text-sm",
      "text-foreground placeholder:text-muted-foreground/70 shadow-xs transition-colors",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring",
      "disabled:cursor-not-allowed disabled:opacity-50",
      className
    ),
    ...props
  }
));
Input.displayName = "Input";
var Label = React2.forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "label",
  {
    ref,
    className: cn(
      "text-sm font-medium text-foreground/90 leading-none",
      className
    ),
    ...props
  }
));
Label.displayName = "Label";
var badgeVariants = cva(
  "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
  {
    variants: {
      tone: {
        neutral: "bg-sand-100 text-sand-700",
        brand: "bg-brand-50 text-brand-700",
        success: "bg-success-soft text-success",
        warning: "bg-warning-soft text-warning",
        danger: "bg-danger-soft text-danger",
        info: "bg-info-soft text-info"
      }
    },
    defaultVariants: { tone: "neutral" }
  }
);
function Badge({ className, tone, dot, children, ...props }) {
  return /* @__PURE__ */ jsxs("span", { className: cn(badgeVariants({ tone }), className), ...props, children: [
    dot && /* @__PURE__ */ jsx("span", { className: "size-1.5 rounded-full bg-current opacity-70" }),
    children
  ] });
}
function Logo({ showWordmark = true, className, ...props }) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2.5", className), ...props, children: [
    /* @__PURE__ */ jsx(
      "img",
      {
        src: "/logo.png",
        alt: "Balatin",
        className: "h-8 w-auto shrink-0"
      }
    ),
    showWordmark && /* @__PURE__ */ jsx("span", { className: "font-display text-lg font-medium tracking-tight text-foreground", children: "Balatin" })
  ] });
}

export { Badge, Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Input, Label, Logo, badgeVariants, buttonVariants, cn };
